/**
 * @typedef UserAsset 사용자 코인 현재 자산
 * @property {UserCoins[]} coins 사용자 코인 자산 리스트
 */

import { authFetch } from "@/plugins/axiosConfig";

/**
 * @typedef UserCoins 사용자 코인 자산 내역
 * @property {string} symbol 코인 명
 * @property {string} position 포지션 (long, short)
 * @property {number} totalPrice 총 매수금액
 * @property {number} totalAve 평균단가
 * @property {number} leverage 레버리지
 * @property {number} mm 마진 유저 증거금? (공식에 필요한 수식)
 *
 */

/**
 * @typedef UserPosition 사용자 포지션
 * @property {String} symbol;
 * @property {String} positionSide; // position 종류 (BOTH, LONG,SHORT)
 * @property {Double} positionAmt; // 수량
 * @property {Double} entryPrice; // 진입가격
 * @property {Double} markPrice; // 거래기준가
 * @property {Double} liquidationPrice; // 청산가
 * @property {Double} unrrealizedProfit; // 미실현이익
 * @property {Integer} leverage;
 * @property {Double} maintMargin; // 마진 증거금
 * @property {Double} positionInitialMargin;
 * @property {Double} openOrderInitialMargin;
 * @property {Long} updateTime;
 */

/**
 * @typedef UserSymbolConfig 사용자 코인 설정 기본정보
 * @property {String} symbol 코인명
 * @property {String} marginType 마진 타입 (isolated , crossed)
 * @property {Boolean} isAuthAddMargin; 자동 마진 추가 여부
 * @property {number} leverage; 레버리지
 * @property {String} maxNotionalValue; 최대 거래 가능 금액
 */

const assetPrefix = "/api/member";

const assetStore = {
  namespaced: true,
  state: {
    init: true,
    asset: {
      asset: "USDT",
      availableBalance: 0, // 사용가능한 잔액
      balance: 0, // 잔고
      crossUnPnl: 0, // cross 미실현 수익
      crossWalletBalance: 0, // 교차마진 잔액
      marginAvailable: false, // 마진 사용 가능 유무
      maxWithdrawAmount: 0, // 최대 인출 금액
      updateTime: 0, // 업데이트 시간
      defaultLeverage: 0,
      minPrice: 0,
      maxPrice: 0,
      tickerSize: 0,
      marketMinQty: 0,
      marketMaxQty: 0,
      minNotional: 0,
    },
    config: [], // 코인별 기본 정보 (레버리지, 마진 타입 등 ...)
    long: {
      coins: [],
    },
    short: {
      coins: [],
    },
  },
  getters: {
    ASSET_USDT: (state) => state.asset,
    ASSET_COIN: (state) => (symbol, position) => {
      return state[position].coins.find((f) => f.symbol === symbol);
    },
    ASSET_CONFIG: (state) => (symbol) => {
      return state.config.find((f) => f.symbol == symbol.toUpperCase());
    },
  },
  mutations: {
    /**
     * long position 코인별 자산 업데이트
     * @param {*} state
     * @param {UserPosition[]} payload
     */
    MU_UPDATE_COIN_LONG: (state, payload) => {
      state.long.coins = payload;
    },
    /**
     * short position 코인별 자산 업데이트
     * @param {*} state
     * @param {UserPosition[]} payload
     */
    MU_UPDATE_COIN_SHORT: (state, payload) => {
      state.short.coins = payload;
    },

    MU_RESET_COIN_ALL: (state) => {
      state.long.coins = [];
      state.short.coins = [];
    },

    /**
     * short position 코인별 자산 업데이트
     * @param {*} state
     * @param {UserPosition[]} payload
     */
    MU_UPDATE_POSITION: (state, payload) => {
      const currentState = { ...state };
      const longPositions = [];
      const shortPositions = [];
      for (let i = 0; i < payload.length; i++) {
        // TODO : 임시
        // position side 가 both 인 경우, long position 으로 간주
        let positionSide = payload[i].positionSide.toLowerCase();
        positionSide = positionSide === "both" ? "long" : positionSide;

        // TODO : 초기화 후 업데이트
        if (Object.prototype.hasOwnProperty.call(currentState, positionSide)) {
          // 포지션 정보 업데이트
          const targetIdx = state[positionSide].coins.findIndex(
            (f) => f.symbol === payload[i].symbol
          );
          if (targetIdx >= 0) {
            state[positionSide].coins[targetIdx] = payload[i];
          } else {
            state[positionSide].coins.push(payload[i]);
          }

          if (positionSide === "long") {
            longPositions.push(payload[i].symbol);
          } else {
            shortPositions.push(payload[i].symbol);
          }
        }
        // TODO : 업데이트 되지 않은 포지션 정보 삭제
        state["long"].coins = state["long"].coins.filter((f) =>
          longPositions.some((s) => s === f.symbol)
        );
        state["short"].coins = state["short"].coins.filter((f) =>
          shortPositions.some((s) => s === f.symbol)
        );
      }
    },

    MU_UPDATE_USDT: (state, payload) => {
      state.init = false;
      state.asset = {
        ...state.asset,
        ...payload,
      };
    },

    /**
     * 사용자 심볼 기본정보 업데이트
     * @param {*} state
     * @param {UserSymbolConfig[]} payload
     */
    MU_UPDATE_SYMBOL_CONFIG: (state, payload) => {
      state.config = [...payload];
    },
    MU_UPDATE_SYMBOL_CONFIG_SINGLE: (state, payload) => {
      for (var i = 0; i < state.config.length; i++) {
        if (state.config[i].symbol === payload.symbol) {
          state.config[i] = { ...payload };
        }
      }
    },
    /**
     * 포지션 마진 타입 변경 (ISOLATED, CROSSED)
     * @param {*} state
     * @param {*} payload
     */
    MU_UPDATE_POSITION_MARGIN_TYPE: (state, payload) => {
      // state.config.
      // TODO : 마진 타입만 변경 (각 코인 LONG, SHORt 포지션 )
      const targetIndex = state.config.findIndex(
        (f) => f.symbol == payload.symbol.toUpperCase()
      );
      if (targetIndex > -1) {
        state.config[targetIndex].marginType = payload.marginType;
      }
    },
  },
  actions: {
    /**
     * long/short position 코인별 자산 api 요청
     * @param {*} param0
     */
    AT_REQ_ALL_COIN: async ({ commit }) => {
      let response = null;
      if (response != null) {
        for (let i = 0; i < response.data.length; i++) {
          if (response.data[i].position == "long") {
            commit("MU_UPDATE_COIN_LONG".response.data[i]);
          } else if (response.data[i].position == "short") {
            commit("MU_UPDATE_COIN_SHORT".response.data[i]);
          }
        }
      }
    },

    AT_REQ_BALANCE: async ({ state, commit }) => {
      if (state.init) {
        const response = await authFetch.get(`/api/asset/all`);
        const { userUSDT, userSymbols } = response.data;
        if (userUSDT) {
          // console.log(userUSDT);
          commit("MU_UPDATE_USDT", userUSDT);
        }
        if (userSymbols.length > 0) {
          // console.log(userSymbols);
          commit("MU_UPDATE_POSITION", userSymbols);
        }
      }
    },

    AT_UPDATE_BALANCE: async ({ commit }) => {
      const response = await authFetch.get(`/api/asset/all`);
      const { userUSDT, userSymbols } = response.data;
      if (userUSDT) {
        // console.log(userUSDT);
        commit("MU_UPDATE_USDT", userUSDT);
      }
      if (userSymbols.length > 0) {
        // console.log(userSymbols);
        commit("MU_UPDATE_POSITION", userSymbols);
      } else {
        commit("MU_RESET_COIN_ALL");
      }
    },

    /**
     * 사용자 심볼 기본 정보 조회
     * @param {*} param0
     */
    AT_UPDATE_SYMBOL_CONFIG: async ({ commit }) => {
      const response = await authFetch.post(`/api/member/config/symbol`);
      const symbolList = response.data;
      commit("MU_UPDATE_SYMBOL_CONFIG", symbolList);
    },

    AT_LOAD_SYMBOL_CONFIG: async ({ commit }, symbol) => {
      const response = await authFetch.get(`/api/member/leverage/${symbol}`);
      const leverageResult = response.data;
      commit("MU_UPDATE_SYMBOL_CONFIG_SINGLE", leverageResult);
    },
    /**
     * 코인 마진 타입 변경 (ISOLATED , CROSSED)
     * @param {*} param0
     * @param {*} payload
     */
    AT_MODIFY_MARGIN_TYPE: async ({ commit }, payload) => {
      const endpoint = `${assetPrefix}/position/marginType`;

      const apiResponse = await authFetch.post(endpoint, payload);
      if (apiResponse.status === 200 || apiResponse.status === 201) {
        const { flag, msg } = apiResponse.data;
        if (flag) {
          // 변경 성공
          const symbol = payload.symbol;
          const marginType = payload.type.toUpperCase();
          commit("MU_UPDATE_POSITION_MARGIN_TYPE", { symbol, marginType });
        } else {
          // 변경 실패
          if (msg === "EXIST POSITION") {
            alert("현재 포지션을 청산후, 마진 타입을 변경해주시기 바랍니다.");
          } else {
            alert("변경 요청중 오류가 발생했습니다.");
          }
        }
      } else {
        if (apiResponse.status === 400) {
          alert("요청 내용을 확인해주시기 바랍니다.");
        } else {
          alert("변경 요청중 오류가 발생했습니다.");
        }
      }
    },

    /**
     * 코인 단일건 자산 업데이트
     * @param {*} param0
     * @param {string} payload 코인 명
     */
    // AT_REQ_SINGLE_COIN: async ({ commit }, payload) => {
    //   let response = null;

    //   if (response != null) {
    //     if (response.data[i].position == "long") {
    //       commit("MU_UPDATE_COIN_LONG", response.data[i]);
    //     } else if (response.data[i].position == "short") {
    //       commit("MU_UPDATE_COIN_SHORT", response.data[i]);
    //     }
    //   }
    // },
  },
};

export default assetStore;
